import { useEffect, useState } from "react";

import {
  loadingPhrasesFileSchema,
  nextLoadingPhrase,
  phrasesForChapter,
  type LoadingPhrasesFile,
} from "@/data/loading-phrases";
import { readLoadingChapter } from "./loadingChapter";

const ROTATE_MS = 1800;
const phrasesByBook = new Map<string, Promise<LoadingPhrasesFile | null>>();

/** Small and separate from the bundles, so it can arrive while they are still loading. */
function fetchLoadingPhrases(bookId: string): Promise<LoadingPhrasesFile | null> {
  const known = phrasesByBook.get(bookId);
  if (known !== undefined) {
    return known;
  }
  const loading = fetch(`/book-data/${bookId}/loading-phrases.json`)
    .then(async (response) => (
      response.ok ? loadingPhrasesFileSchema.parse(await response.json()) : null
    ))
    .catch(() => null);
  phrasesByBook.set(bookId, loading);
  return loading;
}

/** Null until phrases arrive, or for good if they never do. */
export function useLoadingPhrase(
  bookId: string | undefined,
  routeChapterId?: string,
): string | null {
  const [phrase, setPhrase] = useState<string | null>(null);

  useEffect(() => {
    if (bookId === undefined) {
      return;
    }
    let timer = 0;
    let cancelled = false;
    void fetchLoadingPhrases(bookId).then((file) => {
      if (cancelled || file === null) {
        return;
      }
      const phrases = phrasesForChapter(file, routeChapterId ?? readLoadingChapter());
      setPhrase(nextLoadingPhrase(phrases, null));
      timer = window.setInterval(() => {
        setPhrase((current) => nextLoadingPhrase(phrases, current));
      }, ROTATE_MS);
    });
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [bookId, routeChapterId]);

  return phrase;
}
